import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import { User } from 'lucide-react';
import BackArrow from '../../assets/images/Frame_18177.png';
import FemaleGenderImg from '../../assets/images/gender.png';
import MaleGenderImg from '../../assets/images/male_gender.png';
import InactivityTimer from '../../components/InactivityTimer';

const SelectExternalPatient = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [patients, setPatients] = useState([]);
    const [mobileNumber, setMobileNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedPatientId, setSelectedPatientId] = useState(null);

    useEffect(() => {
        // Get patient list from navigation state
        if (location.state?.patients && location.state?.mobileNumber) {
            setPatients(location.state.patients);
            setMobileNumber(location.state.mobileNumber);
        } else {
            // Redirect to login if no data
            navigate('/', { replace: true });
        }
    }, [location, navigate]);

    const calculateAge = (dobStr) => {
        if (!dobStr) return 'N/A';
        
        // Parse DD-MM-YYYY format
        const parts = dobStr.split('-');
        if (parts.length !== 3) return 'N/A';
        
        const birthDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        const today = new Date();
        let years = today.getFullYear() - birthDate.getFullYear();
        let months = today.getMonth() - birthDate.getMonth();
        
        if (months < 0) {
            years--;
            months += 12;
        }
        
        return `${years} Years ${months} Months`;
    };

    const formatDate = (dobStr) => {
        if (!dobStr) return '';
        
        // Parse DD-MM-YYYY format
        const parts = dobStr.split('-');
        if (parts.length !== 3) return dobStr;
        
        const date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const getGenderImage = (genderCode) => {
        if (genderCode === 'FEMALE' || genderCode === 'F') {
            return FemaleGenderImg;
        }
        return MaleGenderImg;
    };

    const getGenderText = (gender) => {
        if (!gender) return 'N/A';
        return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
    };

    const handleProfileSelect = async (patient) => {
        if (loading) return;
        
        setLoading(true);
        setError('');
        setSelectedPatientId(patient.patientId);
        
        try {
            // Get site selection from localStorage
            const registrationSiteCode = localStorage.getItem('site_code');
            
            // Check if patient is from MySQL or API
            if (patient.source === 'mysql') {
                // Patient already exists in MySQL - just send OTP
                // Create externalPatient object from MySQL data
                const externalPatient = {
                    patientId: patient.externalPatientId || patient.patientId,
                    mrno: patient.mrno,
                    prefix: patient.prefix,
                    firstName: patient.firstName,
                    middleName: patient.middleName,
                    lastName: patient.lastName,
                    genderId: patient.genderId,
                    genderCode: patient.genderCode,
                    contactNo: patient.contactNo,
                    email: patient.email || '',
                    dobStr: patient.dob,
                    age: patient.age,
                    siteId: patient.siteId || 2468,
                    addressList: patient.addressList || []
                };

                // Send to backend to update OTP
                const response = await axiosInstance.post('/api/auth/select-external-patient', {
                    mobileNumber: mobileNumber,
                    externalPatient: externalPatient,
                    registrationSiteCode: registrationSiteCode
                });

                if (response.status === 200 || response.status === 201) {
                    // Navigate to OTP entry in Login page
                    navigate('/', { 
                        state: { 
                            mobileNumber: mobileNumber,
                            showOTP: true,
                            message: response.data.message
                        }
                    });
                }
            } else {
                // Patient from API - needs to be inserted or OTP updated
                const externalPatient = {
                    patientId: patient.patientId,
                    mrno: patient.mrno,
                    prefix: patient.prefix,
                    firstName: patient.firstName,
                    middleName: patient.middleName,
                    lastName: patient.lastName,
                    genderId: patient.genderId,
                    genderCode: patient.genderCode,
                    contactNo: patient.contactNo,
                    email: patient.email || '',
                    dobStr: patient.dob,
                    age: patient.age,
                    siteId: patient.siteId || 2468,
                    addressList: patient.addressList || []
                };

                // Send selected patient to backend for insertion/update and OTP
                const response = await axiosInstance.post('/api/auth/select-external-patient', {
                    mobileNumber: mobileNumber,
                    externalPatient: externalPatient,
                    registrationSiteCode: registrationSiteCode
                });

                if (response.status === 200 || response.status === 201) {
                    // Navigate to OTP entry in Login page
                    navigate('/', { 
                        state: { 
                            mobileNumber: mobileNumber,
                            showOTP: true,
                            message: response.data.message
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Error selecting patient:', error);
            setError(error.response?.data?.errors?.general || 'Failed to select patient. Please try again.');
        } finally {
            setLoading(false);
            setSelectedPatientId(null);
        }
    };

    const getFullName = (patient) => {
        const prefix = patient.prefix || '';
        const firstName = patient.firstName || '';
        const middleName = patient.middleName || '';
        const lastName = patient.lastName || '';
        return `${prefix} ${firstName} ${middleName} ${lastName}`.trim();
    };

    return (
        <>
            <InactivityTimer />
            <div>
                {/* Back to Login Button */}
                <div className='flex'>
                    <Link 
                        to="/" 
                        className="flex w-28 mt-6 ml-4 sm:ml-8 lg:ml-14 px-4 py-3 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200" 
                        style={{ fontFamily: 'Roboto', fontSize: '20px', fontWeight: '500', lineHeight: '23.44px' }}
                    >
                        <img src={BackArrow} alt="Back" className="mr-2" style={{ height: '24px', width: '24px' }} />
                        Back
                    </Link>
                    <span className='mt-8 ml-4' style={{ fontFamily: 'Roboto', fontSize: '28px', fontWeight: '600', lineHeight: '32.81px' }}>
                        Select Your Profile
                    </span>
                </div>

                {/* Horizontal Line */}
                <hr className="mt-6 mx-4 sm:mx-8 lg:mx-14 border-t-1 border-gray-300" />

                <div className="flex justify-center mt-10 mb-10">
                    <div className="bg-white shadow-lg rounded-lg flex flex-col w-full max-w-lg">
                        <div className='p-6'>
                            <p className="font-robot text-[#0FA968]" style={{fontWeight: "700", fontSize: "24px"}}>Select Patient Profile</p>
                            <p className="font-robot text-[#42424A]" style={{fontWeight: "400", fontSize: "12px"}}>
                                We found {patients.length} patient{patients.length > 1 ? 's' : ''} registered with this mobile number
                            </p>
                        </div>

                        <hr className="border-t-1 border-gray-300" />
                        
                        <div className='p-6'>
                            {error && (
                                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                                    {error}
                                </div>
                            )}

                            {patients.length === 0 ? (
                                <p className="text-center text-gray-500">No patients found</p>
                            ) : (
                                <ul className="max-h-96 overflow-y-auto">
                                    {patients.map((patient) => (
                                        <li 
                                            key={patient.patientId}
                                            className={`py-3 sm:py-4 p-2 mb-4 rounded-lg bg-[#F7F7F8] hover:bg-[#DAFEEF] hover:border hover:border-[#0FA968] cursor-pointer ${
                                                loading && selectedPatientId === patient.patientId ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}
                                            onClick={() => handleProfileSelect(patient)}
                                        >
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0">
                                                    <div className="ms-2 w-14 h-14 rounded-xl bg-[#0FA968] flex items-center justify-center">
                                                        {loading && selectedPatientId === patient.patientId ? (
                                                            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                                                        ) : (
                                                            <User className="w-8 h-8 text-white" />
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="min-w-0 ms-4">
                                                    <div className='flex items-center'>
                                                        <p className="font-robot text-[20px] text-[#27272A] truncate" style={{ fontWeight: "700" }}>
                                                            {getFullName(patient)}
                                                        </p>
                                                        <img 
                                                            src={getGenderImage(patient.gender)} 
                                                            alt={patient.gender} 
                                                            className="ms-2" 
                                                            style={{ height: '24px', width: '24px' }} 
                                                        />
                                                    </div>
                                                    <p className="font-robot text-[14px] text-[#7A7A7D]" style={{ fontWeight: "400" }}>
                                                        MRN: {patient.mrno}
                                                    </p>
                                                    <div className='flex items-center gap-4 mt-1'>
                                                        <p className="font-robot text-[14px] text-[#7A7A7D]" style={{ fontWeight: "400" }}>
                                                            DOB: {formatDate(patient.dob)}
                                                        </p>
                                                        <p className="font-robot text-[14px] text-[#7A7A7D]" style={{ fontWeight: "400" }}>
                                                            Age: {patient.age || calculateAge(patient.dob)}
                                                        </p>
                                                    </div>
                                                    <p className="font-robot text-[12px] text-[#7A7A7D]" style={{ fontWeight: "400" }}>
                                                        Registered: {patient.registrationDateTime}
                                                    </p>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SelectExternalPatient;
